/**
 * useStudioEditor.ts — Estado do construtor visual do Studio
 *
 * Concentra toda a manipulação do documento em blocos (inserir, mover,
 * duplicar, remover, alterar propriedades) com histórico de desfazer/refazer.
 * O editor de tela (páginas e componentes) não guarda regra nenhuma: apenas
 * chama estas ações.
 *
 * Nenhum bloco é inventado aqui — as propriedades padrão vêm sempre do catálogo
 * único (`@/lib/studio/core`), garantindo uma só fonte de verdade.
 *
 * RELEASE: v1.1.0 - 2026-09-10 - Inserção em posição escolhida (`insertAt`) e
 *          inserção de blocos prontos (`insertPattern`), usados pelo construtor
 *          com pontos de inserção entre os blocos.
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 4)
 */

import { useCallback, useMemo, useState } from 'react';
import {
  createEmptyDocument,
  getBlock,
  getPattern,
  type StudioBlockNode,
  type StudioDocument,
  type StudioPatternNode,
} from '@/lib/studio/core';


/** Limite do histórico para não crescer indefinidamente em sessões longas. */
const LIMITE_HISTORICO = 50;

/** Gera um identificador curto e estável para um bloco novo. */
function novoId(): string {
  return `b-${Math.random().toString(36).slice(2, 10)}`;
}

/** Monta um nó de bloco com as propriedades padrão declaradas no catálogo. */
export function criarNoDeBloco(blockKey: string): StudioBlockNode | null {
  const definicao = getBlock(blockKey);
  if (!definicao) return null;

  const props: Record<string, unknown> = {};
  definicao.props.forEach((p) => {
    if (p.defaultValue !== undefined) props[p.key] = p.defaultValue;
  });

  return {
    id: novoId(),
    type: blockKey,
    props,
    ...(definicao.container ? { children: [] } : {}),
  };
}

/** Converte um nó de padrão (sem identificador) em nó do documento. */
function materializarPadrao(no: StudioPatternNode): StudioBlockNode {
  return {
    id: novoId(),
    type: no.type,
    props: { ...(no.props ?? {}) },
    // Metadados de composição (Fase 8): espaço semântico e origem. Não mudam o
    // desenho — servem para orientar o usuário e avisar de espaço vazio.
    ...(no.slotId ? { slotId: no.slotId } : {}),
    ...(no.slotRole ? { slotRole: no.slotRole } : {}),
    ...(no.sourceComposition ? { sourceComposition: no.sourceComposition } : {}),
    ...(no.sourceCompositionVersion
      ? { sourceCompositionVersion: no.sourceCompositionVersion }
      : {}),
    ...(no.children ? { children: no.children.map(materializarPadrao) } : {}),
  } as StudioBlockNode;
}

/** Retorno do hook do editor. */
export interface StudioEditorApi {
  document: StudioDocument;
  selectedId: string | null;
  selectedNode: StudioBlockNode | null;

  isDirty: boolean;
  canUndo: boolean;
  canRedo: boolean;
  select: (id: string | null) => void;
  addBlock: (blockKey: string) => void;
  /** Insere blocos na posição indicada (0 = antes do primeiro). */
  insertAt: (indice: number, blockKeys: string[]) => void;
  /** Insere um bloco pronto (padrão) na posição indicada. */
  insertPattern: (patternKey: string, indice?: number) => void;

  updateProp: (id: string, propKey: string, value: unknown) => void;
  moveBlock: (id: string, direcao: -1 | 1) => void;
  duplicateBlock: (id: string) => void;
  removeBlock: (id: string) => void;
  replaceDocument: (doc: StudioDocument, marcarSujo?: boolean) => void;
  undo: () => void;
  redo: () => void;
  markSaved: () => void;
}

/**
 * Estado do editor.
 * @param documentoInicial documento carregado do rascunho (ou vazio)
 */
export function useStudioEditor(documentoInicial?: StudioDocument | null): StudioEditorApi {
  const [doc, setDoc] = useState<StudioDocument>(
    () => documentoInicial ?? createEmptyDocument(),
  );
  const [passado, setPassado] = useState<StudioDocument[]>([]);
  const [futuro, setFuturo] = useState<StudioDocument[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  /** Aplica uma alteração registrando o estado anterior no histórico. */
  const aplicar = useCallback(
    (transformacao: (atual: StudioDocument) => StudioDocument) => {
      setDoc((atual) => {
        const proximo = transformacao(atual);
        if (proximo === atual) return atual;
        setPassado((p) => [...p, atual].slice(-LIMITE_HISTORICO));
        setFuturo([]);
        setIsDirty(true);
        return proximo;
      });
    },
    [],
  );

  const addBlock = useCallback(
    (blockKey: string) => {
      const no = criarNoDeBloco(blockKey);
      if (!no) return;
      aplicar((atual) => ({ ...atual, blocks: [...atual.blocks, no] }));
      setSelectedId(no.id);
    },
    [aplicar],
  );

  /** Insere blocos do catálogo na posição escolhida. */
  const insertAt = useCallback(
    (indice: number, blockKeys: string[]) => {
      const nos = blockKeys
        .map((chave) => criarNoDeBloco(chave))
        .filter((no): no is StudioBlockNode => Boolean(no));
      if (!nos.length) return;
      aplicar((atual) => {
        const blocos = [...atual.blocks];
        const posicao = Math.max(0, Math.min(indice, blocos.length));
        blocos.splice(posicao, 0, ...nos);
        return { ...atual, blocks: blocos };
      });
      setSelectedId(nos[0].id);
    },
    [aplicar],
  );

  /** Insere um bloco pronto (um ou mais blocos já preenchidos). */
  const insertPattern = useCallback(
    (patternKey: string, indice?: number) => {
      const padrao = getPattern(patternKey);
      if (!padrao) return;
      const nos = padrao.build().map(materializarPadrao);
      if (!nos.length) return;
      aplicar((atual) => {
        const blocos = [...atual.blocks];
        const posicao = Math.max(0, Math.min(indice ?? blocos.length, blocos.length));
        blocos.splice(posicao, 0, ...nos);
        return { ...atual, blocks: blocos };
      });
      setSelectedId(nos[0].id);
    },
    [aplicar],
  );



  const updateProp = useCallback(
    (id: string, propKey: string, value: unknown) => {
      aplicar((atual) => ({
        ...atual,
        blocks: atual.blocks.map((b) =>
          b.id === id ? { ...b, props: { ...b.props, [propKey]: value } } : b,
        ),
      }));
    },
    [aplicar],
  );

  const moveBlock = useCallback(
    (id: string, direcao: -1 | 1) => {
      aplicar((atual) => {
        const indice = atual.blocks.findIndex((b) => b.id === id);
        const destino = indice + direcao;
        if (indice < 0 || destino < 0 || destino >= atual.blocks.length) return atual;
        const blocos = [...atual.blocks];
        const [movido] = blocos.splice(indice, 1);
        blocos.splice(destino, 0, movido);
        return { ...atual, blocks: blocos };
      });
    },
    [aplicar],
  );

  const duplicateBlock = useCallback(
    (id: string) => {
      const copiaId = novoId();
      aplicar((atual) => {
        const indice = atual.blocks.findIndex((b) => b.id === id);
        if (indice < 0) return atual;
        const original = atual.blocks[indice];
        const copia: StudioBlockNode = {
          ...original,
          id: copiaId,
          props: { ...original.props },
        };
        const blocos = [...atual.blocks];
        blocos.splice(indice + 1, 0, copia);
        return { ...atual, blocks: blocos };
      });
      setSelectedId(copiaId);
    },
    [aplicar],
  );

  const removeBlock = useCallback(
    (id: string) => {
      aplicar((atual) => ({ ...atual, blocks: atual.blocks.filter((b) => b.id !== id) }));
      setSelectedId((atual) => (atual === id ? null : atual));
    },
    [aplicar],
  );

  const replaceDocument = useCallback(
    (novo: StudioDocument, marcarSujo = true) => {
      setDoc((atual) => {
        setPassado((p) => [...p, atual].slice(-LIMITE_HISTORICO));
        setFuturo([]);
        return novo;
      });
      setIsDirty(marcarSujo);
      setSelectedId(null);
    },
    [],
  );

  const undo = useCallback(() => {
    setPassado((p) => {
      if (!p.length) return p;
      const anterior = p[p.length - 1];
      setDoc((atual) => {
        setFuturo((f) => [atual, ...f].slice(0, LIMITE_HISTORICO));
        return anterior;
      });
      setIsDirty(true);
      return p.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuturo((f) => {
      if (!f.length) return f;
      const proximo = f[0];
      setDoc((atual) => {
        setPassado((p) => [...p, atual].slice(-LIMITE_HISTORICO));
        return proximo;
      });
      setIsDirty(true);
      return f.slice(1);
    });
  }, []);

  const markSaved = useCallback(() => setIsDirty(false), []);

  const selectedNode = useMemo(
    () => doc.blocks.find((b) => b.id === selectedId) ?? null,
    [doc.blocks, selectedId],
  );

  return {
    document: doc,
    selectedId,
    selectedNode,
    isDirty,
    canUndo: passado.length > 0,
    canRedo: futuro.length > 0,
    select: setSelectedId,
    addBlock,
    insertAt,
    insertPattern,
    updateProp,
    moveBlock,
    duplicateBlock,
    removeBlock,
    replaceDocument,
    undo,
    redo,
    markSaved,
  };
}
